// src/pages/Landing.jsx — with CTA pulse, founder quote, and curved dividers
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiChevronDown, FiSettings, FiX } from "react-icons/fi";
import { Link as ScrollLink } from "react-scroll";
import { saveAs } from "file-saver";
import { decryptAES } from "../utils/cryptoUtils";
import TrustBadges from "../components/TrustBadges";

function Landing({ supabase, user }) {
  const navigate = useNavigate();
  const [showArrow, setShowArrow] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowArrow(window.scrollY <= 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Exit-intent (gentle): show once per browser for the week
  useEffect(() => {
    const key = "vintariai_exit_intent_seen";
    const seen = localStorage.getItem(key);
    const handler = () => {
      if (!seen) {
        setShowExitIntent(true);
        localStorage.setItem(key, String(Date.now()));
      }
    };
    if (window.innerWidth > 1024) {
      document.addEventListener("mouseleave", handler);
    }
    return () => document.removeEventListener("mouseleave", handler);
  }, []);

  const goToSignup = () => navigate("/strategist");

  /* ---------- Export My Data (unchanged) ---------- */
  const handleExportMyData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not found.");

      const { data: chatSessions, error: sessionsError } = await supabase
        .from("chat_sessions")
        .select("id, title, created_at")
        .eq("user_id", user.id);
      if (sessionsError) throw sessionsError;

      let fullExport = "Your VintariAI Chat History\n\n";
      for (const session of chatSessions) {
        const decryptedTitle = session.title ? await decryptAES(session.title) : "Untitled Session";
        fullExport += "==================================================\n";
        fullExport += `Session: ${decryptedTitle}\n`;
        fullExport += `Created At: ${new Date(session.created_at).toLocaleString()}\n`;
        fullExport += "==================================================\n\n";

        const { data: messages, error: msgError } = await supabase
          .from("chat_history")
          .select("prompt, response, created_at")
          .eq("session_id", session.id)
          .order("created_at", { ascending: true });
        if (msgError) throw msgError;

        for (const turn of messages) {
          const ts = new Date(turn.created_at).toLocaleTimeString();
          if (turn.prompt) {
            const p = await decryptAES(turn.prompt);
            fullExport += `[${ts}] You:\n${p}\n\n`;
          }
          if (turn.response) {
            const r = await decryptAES(turn.response);
            fullExport += `[${ts}] Strategist:\n${r}\n\n`;
          }
        }
        fullExport += `--- End of Session ---\n\n`;
      }

      const blob = new Blob([fullExport], { type: "text/plain;charset=utf-8" });
      saveAs(blob, "vintariai_chat_export.txt");
      alert("✅ Your data has been exported successfully.");
    } catch (err) {
      console.error("❌ Export failed:", err);
      alert(`Something went wrong while exporting your data: ${err.message}`);
    }
  };

  /* ---------- Delete Account (unchanged) ---------- */
  const handleDeleteAccount = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("No session found");

 const API = "https://vintariai-backend-1.onrender.com"; 
 const response = await fetch(`${API}/api/delete-user`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
  });
      const result = await response.json();
      if (!response.ok) {
        console.error("❌ Account deletion failed:", result.error);
        alert(`❌ Failed to delete account: ${result.error || "Unknown error"}`);
        return;
      }

      await supabase.auth.signOut();
      alert("✅ Your account has been deleted.");
      setTimeout(() => (window.location.href = "/"), 800);
    } catch (error) {
      console.error("❌ Account deletion failed:", error.message);
      alert("Something went wrong deleting your account. Please try again.");
    }
  };

  return (
    <div className="bg-black text-white relative overflow-hidden">
      {/* CTA pulse keyframes (lightweight, page-local) */}
      <style>{`
        @keyframes ctaPulse {
          0% { box-shadow: 0 0 0 0 rgba(124, 58, 237, 0.55); }
          70% { box-shadow: 0 0 0 14px rgba(124, 58, 237, 0); }
          100% { box-shadow: 0 0 0 0 rgba(124, 58, 237, 0); }
        }
        .cta-pulse {
          animation: ctaPulse 2.2s ease-out infinite;
        }
      `}</style>

      {/* Decorative purple shapes (subtle, performance-friendly) */}
      <div
        className="pointer-events-none absolute -top-24 -left-20 h-[28rem] w-[28rem] rounded-full blur-3xl opacity-30"
        style={{ background: "radial-gradient(circle at center, #7c3aed, transparent 60%)" }}
      />
      <div
        className="pointer-events-none absolute bottom-10 -right-20 h-[22rem] w-[22rem] rounded-full blur-3xl opacity-20"
        style={{ background: "radial-gradient(circle at center, #8b5cf6, transparent 60%)" }}
      />

      <div className="relative z-10">
        {/* Delete confirm modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-gray-800 text-white rounded-lg p-6 max-w-sm w-full shadow-xl">
              <h2 className="text-lg font-bold mb-4">Delete Your Account?</h2>
              <p className="text-sm text-white/80 mb-6">
                This action is permanent and cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 bg-white/10 rounded hover:bg-white/20 transition text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Exit-intent modal (shown once) */}
        {showExitIntent && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
            <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-2xl relative">
              <button
                onClick={() => setShowExitIntent(false)}
                className="absolute right-3 top-3 text-white/70 hover:text-white"
              >
                <FiX size={18} />
              </button>
              <h3 className="text-xl font-bold">Before you go…</h3>
              <p className="text-white/80 mt-2">
                Get <span className="font-semibold">early access</span> to your private AI strategist and get 5
                strategic questions free. Seats are limited weekly.
              </p>
              <button
                onClick={() => {
                  setShowExitIntent(false);
                  goToSignup();
                }}
                className="mt-5 w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-lg transition cta-pulse"
              >
                Join Free — Get Instant Access
              </button>
              <p className="text-xs text-white/60 mt-2 text-center">No spam · No sharing · Cancel anytime</p>
            </div>
          </div>
        )}

        {/* Top Bar */}
        <div className="flex justify-between items-center p-4 relative z-20">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate("/privacy")}
              className="text-sm bg-white/10 text-white px-4 py-2 rounded-lg hover:bg-white/20 transition"
            >
              Privacy
            </button>
            <button
              onClick={() => navigate("/faq")}
              className="text-sm bg-white/10 text-white px-4 py-2 rounded-lg hover:bg-white/20 transition"
            >
              FAQ
            </button>
          </div>

          {/* Settings Dropdown */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="p-2 bg-white/10 text-white rounded-full hover:bg-white/20 transition"
            >
              <FiSettings size={20} />
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 rounded-md bg-white/10 text-sm text-white border border-white/20 backdrop-blur-md shadow-lg z-50">
                <button
                  onClick={() => {
                    handleExportMyData();
                    setDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-white/20 transition"
                >
                  📄 Export My Data
                </button>
                <a
                  href="https://docs.google.com/forms/d/e/1FAIpQLScYdVuvpyoKV3q04QaVoedAXePxGFOVeTP5uUC0EOFIpyNkYA/viewform?usp=header"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setDropdownOpen(false)}
                  className="block w-full text-left px-4 py-2 hover:bg-white/20 transition"
                >
                  📨 Contact Support
                </a>
                <button
                  onClick={() => {
                    navigate("/upgrade");
                    setDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-white/20 transition"
                >
                  🚀 Want to Upgrade?
                </button>
                <button
                  onClick={() => {
                    navigate("/terms");
                    setDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-white/20 transition"
                >
                  📜 Terms & Conditions
                </button>
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    setShowConfirmModal(true);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-white/20 transition"
                >
                  🗑️ Delete Account
                </button>
              </div>
            )}
          </div>
        </div>

        {/* === HERO === */}
        <section className="min-h-[80vh] md:min-h-screen flex flex-col items-center justify-center text-center px-4 sm:px-6 relative">
          {/* spotlight glow */}
          <div
            className="pointer-events-none absolute top-1/3 h-72 w-72 md:h-96 md:w-96 rounded-full blur-3xl opacity-30"
            style={{ background: "radial-gradient(circle at center, #7c3aed, transparent 60%)" }}
          />

          <motion.h1
            className="text-4xl md:text-6xl font-extrabold text-[#EDEDED] max-w-4xl md:leading-snug"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            Your Private AI Strategist for <span className="text-purple-400">Winning Decisions</span>
          </motion.h1>

          <motion.p
            className="text-lg text-white/80 mt-6 max-w-2xl"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
          >
            Make sharper moves, expose blind spots, and think like a top strategist — in total privacy.
          </motion.p>

          <motion.div
            className="mt-8 relative group"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.25, ease: "easeOut" }}
          >
            <button
              data-track="cta_start_free"
              onClick={goToSignup}
              className="relative bg-purple-600 hover:bg-purple-700 text-white font-semibold px-8 py-4 rounded-lg shadow-lg text-lg transition duration-200 hover:shadow-purple-700/30 cta-pulse"
            >
              Start Free — Get Instant Access
            </button>
            <div className="text-xs text-white/60 mt-2">No spam · No sharing · Cancel anytime</div>
            <div className="text-xs text-purple-300/80 mt-1">Early access — limited weekly seats</div>
          </motion.div>



          {/* What you'll get strip */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 w-full max-w-4xl">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-sm font-semibold">🔍 Instant Clarity</div>
              <div className="text-sm text-white/70 mt-1">Turn any challenge into a clear plan.</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-sm font-semibold">⚡ Faster Decisions</div>
              <div className="text-sm text-white/70 mt-1">Boardroom-grade answers in minutes.</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-sm font-semibold">🔒 Private by Design</div>
              <div className="text-sm text-white/70 mt-1">Encrypted. No training. You control data.</div>
            </div>
          </div>

          {/* Founder micro-quote (adds authenticity) */}
          <div className="mt-8 max-w-3xl mx-auto text-center">
            
            <p className="italic text-white/80">
“VintariAI transforms complex decisions into clear, defensible strategies, so you move first, fast, and with conviction.”            </p>
            <p className="text-white/60 text-sm mt-2">— Maxim D., Founder</p>
          </div>

          {/* Social proof / credibility strip — high-impact design */}
          <div className="mt-8 max-w-4xl mx-auto relative">
            {/* purple glow backdrop */}
            <div
              className="pointer-events-none absolute -inset-2 rounded-2xl blur-2xl opacity-30"
              style={{ background: "radial-gradient(60% 60% at 50% 50%, #7c3aed55, transparent)" }}
            />
            <div className="relative border border-white/10 bg-white/5 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
              <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
                <li className="text-xs sm:text-sm text-white/80 bg-black/20 border border-white/10 rounded-lg px-4 py-2">
                  Adopted by top consultants replacing outdated MBA frameworks
                </li>
                <li className="text-xs sm:text-sm text-white/80 bg-black/20 border border-white/10 rounded-lg px-4 py-2">
                  Credited with accelerating billion-dollar product launches
                </li>
                <li className="text-xs sm:text-sm text-white/80 bg-black/20 border border-white/10 rounded-lg px-4 py-2">
                  Inspired by principles shared at global innovation forums
                </li>
              </ul>

              <div className="mt-2 text-center text-xs sm:text-sm text-white/70">
                Featured in <em>“What If I Am Not Elon Musk”</em> — the new playbook for thinking like a founder
              </div>
            </div>
          </div>

          {/* Scroll cue */}
          {showArrow && (
            <ScrollLink
              to="value-section"
              smooth
              duration={600}
              className="mt-10 cursor-pointer flex flex-col items-center"
            >
              <FiChevronDown className="animate-bounce text-white text-3xl" />
              <FiChevronDown className="animate-bounce text-white text-2xl -mt-2" />
            </ScrollLink>
          )}
        </section>

        {/* Curved divider */}
        <div className="relative">
          <svg className="w-full h-14" viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path fill="#0b0b0b" d="M0,0L60,10C120,20,240,40,360,45C480,50,600,40,720,25C840,10,960,-5,1080,5C1200,15,1320,45,1380,60L1440,75V0H0Z" />
          </svg>
        </div>

        {/* === EXAMPLE PROMPTS === */}
        <section id="value-section" className="py-16 px-6 text-center bg-[#0b0b0b]">
          <h2 className="text-3xl font-bold">What can you ask the Strategist?</h2>
          <p className="text-white/70 mt-2 max-w-2xl mx-auto">
            Get frameworks, risks, and next moves — without prompt engineering.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8 max-w-6xl mx-auto text-left">
            {[
              { title: "Find blind spots", prompt: "What are the top 3 risks in my current plan and how do I mitigate them?" },
              { title: "Position to win", prompt: "How can I position my product to stand out vs. bigger competitors?" },
              { title: "Reframe fast", prompt: "Reframe this idea from a customer’s perspective and suggest a better pitch." },
              { title: "Decide faster", prompt: "Give me the next 3 moves to validate this idea in 7 days." },
            ].map((item, i) => (
              <div key={i} className="bg-gray-900 p-5 rounded-xl border border-white/10 shadow-lg">
                <div className="text-sm uppercase tracking-wide text-white/60">{item.title}</div>
                <div className="text-white mt-2 text-sm">{item.prompt}</div>
              </div>
            ))}
          </div>

          <button
            onClick={goToSignup}
            className="mt-8 bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-lg transition cta-pulse"
          >
            Try Your 5 questions — Free
          </button>
        </section>

        {/* Curved divider */}
        <div className="relative">
          <svg className="w-full h-14" viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path fill="#0a0a0a" d="M0,75L60,60C120,45,240,15,360,5C480,-5,600,5,720,20C840,35,960,55,1080,60C1200,65,1320,55,1380,50L1440,45V100H0Z" />
          </svg>
        </div>

        {/* === FEATURE / EDGE === */}
        <section className="py-24 px-6 bg-gradient-to-b from-black to-gray-900 text-center">
          <h2 className="text-3xl font-bold text-white">Built for Clarity, Speed, and Absolute Control</h2>
          <p className="text-white/70 mt-2 max-w-2xl mx-auto">
            Every feature engineered to give you decisive insight — instantly, securely, and without compromise.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12 max-w-6xl mx-auto text-left">
            <div className="bg-gray-900 p-6 rounded-xl border border-white/10 shadow-lg">
              <h3 className="text-lg font-bold text-white">Strategic Modes On Demand</h3>
              <p className="text-white/70 mt-3 text-sm">
                Switch between thinking styles for the moment: quick clarity or bold innovation.
              </p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-white/10 shadow-lg">
              <h3 className="text-lg font-bold text-white">Proven Frameworks, Zero Delay</h3>
              <p className="text-white/70 mt-3 text-sm">
                Boardroom frameworks without the coursework — ready in one click.
              </p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-white/10 shadow-lg">
              <h3 className="text-lg font-bold text-white">One Hub, Total Clarity</h3>
              <p className="text-white/70 mt-3 text-sm">
                Insights and playbooks centralized for momentum in one command hub.
              </p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-white/10 shadow-lg">
              <h3 className="text-lg font-bold text-white">Your Strategy Stays Yours</h3>
              <p className="text-white/70 mt-3 text-sm">
                No leaks. No sharing. Your data never trains models.
              </p>
            </div>
          </div>
        </section>

        {/* Curved divider */}
        <div className="relative">
          <svg className="w-full h-14" viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path fill="#000000" d="M0,0L60,5C120,10,240,20,360,30C480,40,600,50,720,55C840,60,960,60,1080,50C1200,40,1320,20,1380,10L1440,0V100H0Z" />
          </svg>
        </div>

        {/* === TRUST / PRIVACY === */}
        <section className="py-12 px-6 text-center bg-black">
          <h3 className="text-2xl font-bold">Why professionals choose VintariAI</h3>
          <p className="text-white/70 mt-2 max-w-2xl mx-auto">
            End-to-end encryption, private by design, full data control, and global compliance.
          </p>
          <TrustBadges />
          <button
            onClick={goToSignup}
            className="mt-8 bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-lg transition cta-pulse"
          >
            Start Free — Get Instant Access
          </button>
        </section>
      </div>
    </div>
  );
}

export default Landing;
